import React from 'react'
import Flex from 'typescript-styled-flex'
import { useTranslation } from 'react-i18next'
import {
  getFormValues,
  InjectedFormProps,
  isSubmitting,
  reduxForm,
} from 'redux-form'
import { connect } from 'react-redux'
import { addDays } from 'date-fns'
import { Dispatch } from 'redux'
import { bindToReduxForm } from '../../../actions/form'
import { Form, FormGroup } from '../../../components/FormBuilder'
import DateRangeContainerWithDateFields from '../../../components/FormBuilder/DateRangeInput/DateRangeContainerWithDateFields'
import { is } from '../../../components/MediaQuery'
import { IEvent } from '../../../models/Event'
import { IRoom } from '../../../models/Room'
import { IStore } from '../../../reducers'
import { ICreateReservationFormValues } from '../../Form/CreateReservation/CreateReservation'
import Button from '../../../components/Template/Button'
import requestCopyReservation from '../../../actions/bookingCalendar/event/copyReservation'
import { fireToastError } from '../../../actions/toasts/fireToast'
import SpinningIcon from '../../../components/Template/SpinningIcon'
import { getRoomsAvailableWithinDateRangeWithoutFormEvent } from '../../../reducers/bookingCalendar/selectors/rooms'
import RoomsCheckboxList from '../ReservationModal/Components/RoomsCheckboxList'
import useSize from '../../../hooks/useSize'

function CopyForm({
  copy,
  fireToast,
  copyValues,
  rooms,
  isLoading,
  handleSubmit,
}: ICopyFormProps & ICopyFormState & ICopyFormDispatch & InjectedFormProps) {
  const [t] = useTranslation()
  const isDesktop = is.desktop()
  const [error, setError] = React.useState(false)
  const [disabled, setDisabled] = React.useState(true)
  const size = useSize()

  /* eslint-disable */
  React.useEffect(() => {
    setDisabled(
      !(copyValues && copyValues.room && copyValues.room.some(r => !!r)),
    )
  }, [copyValues])

  function normalizeFromDate(value, prev, all) {
    if (value >= all.dateTo) {
      fireToast(t('reservation_error_end_date_before_start_date'))
      setError(true)
      return prev
    } else {
      setError(false)
      return value
    }
  }

  function normalizeToDate(value, prev, all) {
    if (value <= all.dateFrom) {
      fireToast(t('reservation_error_end_date_before_start_date'))
      setError(true)
      return prev
    } else {
      setError(false)
      return value
    }
  }

  return (
    <Form onSubmit={handleSubmit(copy)}>
      <Flex
        column={true}
        style={{ padding: `0px ${16 * size}px`, overflow: 'hidden' }}
      >
        <Flex style={{ width: '100%', overflow: 'hidden' }} row={true}>
          <DateRangeContainerWithDateFields containerStyles={{ zIndex: 5000 }}>
            {({ DateFromInput, DateToInput }) => (
              <>
                <DateFromInput
                  size={size}
                  readonly={true}
                  name={'dateFrom'}
                  sized={true}
                  disableMobileResizing={true}
                  isCenterOnMobile={!isDesktop}
                  normalize={normalizeFromDate}
                  errorMsg={error}
                  label={t('reservation_from')}
                  width={'100%'}
                  changeDateOnDisable={true}
                />
                <DateToInput
                  size={size}
                  readonly={true}
                  isCenterOnMobile={!isDesktop}
                  wrapperStyle={{ marginLeft: `${20 * size}px` }}
                  name={'dateTo'}
                  normalize={normalizeToDate}
                  errorMsg={error}
                  sized={true}
                  disableMobileResizing={true}
                  label={t('reservation_to')}
                  width={'100%'}
                  changeDateOnDisable={true}
                />
              </>
            )}
          </DateRangeContainerWithDateFields>
        </Flex>

        <FormGroup.Render
          size={size}
          styleGroup={{ overflow: 'hidden' }}
          style={{ overflow: 'hidden' }}
          column={true}
          title={t('accommodation')}
        >
          <RoomsCheckboxList size={size} rooms={rooms} />
        </FormGroup.Render>
        <Button
          size={size}
          as={'button'}
          style={{
            marginBottom: `${16 * size}px`,
            width: '100%',
            overflow: 'hidden',
          }}
          primary={!isLoading}
          disabled={isLoading || disabled}
        >
          {isLoading ? <SpinningIcon /> : t('reservation_copy_btn')}
        </Button>
      </Flex>
    </Form>
  )
}

const mapStateToProps = (state: IStore, props: ICopyFormProps) => {
  const copyValues: any = getFormValues('copyForm')(state)
  const values = props.eventData

  return {
    copyValues: copyValues,
    isLoading: isSubmitting('copyForm')(state),
    rooms: copyValues
      ? getRoomsAvailableWithinDateRangeWithoutFormEvent(state, {
          eventData: values,
          dateTo: copyValues.dateTo,
          dateFrom: copyValues.dateFrom,
        })
      : [],
    initialValues: {
      dateFrom: values.dateFrom,
      dateTo: values.dateTo ? values.dateTo : addDays(values.dateFrom, 1),
      room: [],
      eventData: props.eventData,
      eventId: props.eventId,
    },
  }
}

const mapDispatchToProps = (dispatch: Dispatch): ICopyFormDispatch => ({
  copy: bindToReduxForm(requestCopyReservation),
  fireToast: (message) =>
    dispatch(fireToastError(message, undefined, undefined, 'copyDateError')),
})

const ReduxCopyForm = reduxForm<
  ICopyFormValues,
  ICopyFormProps & ICopyFormState & ICopyFormDispatch
>({
  form: 'copyForm',
  destroyOnUnmount: true,
  enableReinitialize: true,
  onSubmitSuccess: (result, dispatch, props) => {
    props.onSuccess()
  },
})(CopyForm)

const CopyReservationForm = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ReduxCopyForm)
export default CopyReservationForm

interface ICopyFormProps {
  eventData: ICreateReservationFormValues
  eventId: IEvent['id']
  onSuccess: () => void
}

interface ICopyFormState {
  copyValues: ICopyFormValues
  rooms: IRoom[]
  isLoading: boolean
}

interface ICopyFormDispatch {
  fireToast: (message: string) => void
  copy: (values: ICopyFormValues, dispatch: Dispatch) => any
}

export interface ICopyFormValues {
  dateFrom: Date
  dateTo?: Date
  room: boolean[]
  eventData: ICreateReservationFormValues
  eventId: IEvent['id'] | null
}
